import React, { useState } from 'react';
import { Question, isQuestionContent } from "@/types/test";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { RefreshCw, Loader2, ChevronDown } from "lucide-react";

interface RegenerationFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: Question;
  questionNumber?: number;
  onRegenerate: (feedback: string) => Promise<void>;
}

export function RegenerationFeedbackDialog({
  open,
  onOpenChange,
  question,
  questionNumber,
  onRegenerate
}: RegenerationFeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showExamples, setShowExamples] = useState(false);

  const handleRegenerate = async () => {
    if (!feedback.trim()) return;
    
    try {
      setIsRegenerating(true);
      await onRegenerate(feedback);
      setFeedback('');
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancel = () => {
    setFeedback('');
    onOpenChange(false);
  };

  // Safe content access with type checking
  const questionContent = isQuestionContent(question.content) ? question.content : { question: '' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <RefreshCw className="h-5 w-5 text-secondary" />
            Regenerate Question {questionNumber ? `#${questionNumber}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Question Display */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-foreground">
              Current Question
            </Label>
            <Card className="bg-card border-border rounded-sm shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</span>
                  <span className="text-sm text-foreground capitalize">{question.question_type.replace('_', ' ')}</span>
                </div>
                
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Question</span>
                  <p className="text-sm text-foreground leading-relaxed">
                    {questionContent.question || 'No question text'}
                  </p>
                </div>

                {question.question_type === 'multiple_choice' && questionContent.options && (
                  <div className="space-y-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Options</span>
                    <div className="space-y-1">
                      {questionContent.options.map((option: string, index: number) => (
                        <div key={index} className={`text-sm px-2 py-1 rounded-sm ${
                          index === questionContent.correctOption 
                            ? 'bg-secondary/10 text-secondary font-medium' 
                            : 'text-foreground'
                        }`}>
                          {option} {index === questionContent.correctOption ? '(Correct)' : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
                  <div className="text-center">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">Skill</span>
                    <span className="text-sm text-foreground">{question.skill || 'Not specified'}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">Difficulty</span>
                    <span className="text-sm text-foreground capitalize">{question.difficulty || 'Not specified'}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">Time</span>
                    <span className="text-sm text-foreground">{question.time_limit || 'Not specified'}s</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Feedback Input */}
          <div className="space-y-3">
            <Label htmlFor="feedback" className="text-sm font-semibold text-foreground">
              What should we improve?
            </Label>
            <Textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Describe the improvements you'd like to see..."
              className="min-h-[120px] resize-none bg-card border-border rounded-sm focus:ring-secondary focus:border-secondary"
              disabled={isRegenerating}
            />
            <Collapsible open={showExamples} onOpenChange={setShowExamples}>
              <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <span className="font-medium">Examples</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showExamples ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down data-[state=closed]:animate-accordion-up">
                <ul className="space-y-1 ml-2 mt-2 text-xs text-muted-foreground">
                  <li>• Make it more practical and hands-on</li>
                  <li>• Focus on specific technologies or frameworks</li>
                  <li>• Adjust difficulty level (too easy/hard)</li>
                  <li>• Add real-world scenario context</li>
                  <li>• Target specific experience levels</li>
                </ul>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>

        <DialogFooter className="gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isRegenerating}
            className="border-border hover:bg-muted"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={!feedback.trim() || isRegenerating}
            className="min-w-[140px] bg-secondary hover:bg-secondary-hover text-secondary-foreground relative overflow-hidden before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-gradient-to-r before:from-white/0 before:via-white/20 before:to-white/0 before:translate-x-[-100%] hover:before:animate-gloss"
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate Question
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}